/**
 * The in-browser data validator — ADR-127 D3, layer 1.
 *
 * ENGINE CHOICE: Ajv 8, via `ajv/dist/2020`.
 * -----------------------------------------
 * D3 requires validation "as the operator types", and rules out the shell-out MC uses for
 * activities: `python3 validate-activities.py` per keystroke degrades "real-time" to "on
 * blur, over the network". So layer 1 needs an engine in the browser.
 *
 * Ajv is the choice because it is the only mature Draft 2020-12 implementation in JS, and —
 * more importantly for this decision — its DEFAULT posture already matches the one
 * ADR-129 D3c fixed for the Python side:
 *
 *   NO FORMAT ASSERTION. D3c: Debian 12 packages none of jsonschema's format extras, so an
 *   image-side format checker silently ignores `date-time`, `duration`, `uri` and
 *   `json-pointer` — which would mean CI and the MC image disagreeing about what "valid"
 *   means. The platform therefore passes no format checker at all, and "a constraint that
 *   matters is spelled with `pattern`". Ajv validates no formats unless `ajv-formats` is
 *   installed, so the browser inherits the same rule by construction rather than by
 *   configuration someone could flip. `ajv-formats` is deliberately NOT a dependency.
 *
 * WHAT THIS EVALUATES, AND WHY IT IS NOT THE RAW SCHEMA
 * ----------------------------------------------------
 * The layer-2 annotations only have effects once `compile_schema()` has materialised them:
 * `x-unknown-sentinel` into a type-preserving `oneOf`, `x-provenance` into `required`,
 * `x-valid-time` into the records' required interval fields. ADR-125 D12 forbids
 * hand-writing those per schema, and a JS reimplementation of the compiler would be a second
 * generator — which is what ADR-129 D6 exists to prevent. So the caller passes the COMPILED
 * schema, served by `validate-reference-data.py --compiled`, and this module only evaluates.
 *
 * That is also what makes parity checkable: both engines are handed the same compiled bytes,
 * so a disagreement is the engine's rather than the compiler's.
 *
 * PARITY IS ON VERDICT AND LOCATION, NOT MESSAGE
 * ----------------------------------------------
 * `schemaValidator.parity.test.ts` runs this engine and `jsonschema==4.10.3` over one corpus
 * and asserts they agree on valid/invalid and on the instance POINTER of each error. Error
 * TEXT is deliberately not compared — two engines will never phrase an error identically, and
 * asserting on prose would make the guard fail on a wording change while missing a real
 * behavioural divergence. What an operator needs is the same verdict at the same field.
 */

import Ajv2020 from 'ajv/dist/2020'
import type { ErrorObject, ValidateFunction } from 'ajv'

/** One validation finding, in the shape the Data area's form binds to. */
export interface SchemaFinding {
  /** RFC 6901 pointer into the INSTANCE — the field the form highlights. */
  pointer: string
  /** The failing keyword (`type`, `required`, `enum`, `maxItems`, `oneOf`, …). Stable
   *  across engines, unlike the message, which is why parity asserts on this. */
  keyword: string
  /** Human text, from Ajv. Shown to the operator; never asserted on in parity. */
  message: string
}

export interface SchemaVerdict {
  valid: boolean
  findings: SchemaFinding[]
}

/**
 * One Ajv instance, configured once.
 *
 * - `allErrors`: the form highlights every bad field, not just the first. A validator that
 *   stops at the first error makes an operator fix a list one reload at a time.
 * - `strict: false`: a compiled schema still carries `$comment` and may carry keywords Ajv
 *   would otherwise refuse to compile. Strict mode would reject the platform's OWN generated
 *   documents, turning "valid data" into "unloadable schema".
 * - NO `ajv-formats`. See the header — this is ADR-129 D3c, inherited by default.
 */
function makeAjv(): Ajv2020 {
  return new Ajv2020({ allErrors: true, strict: false, validateFormats: false })
}

const compiledCache = new WeakMap<object, ValidateFunction>()

/**
 * True iff `format` is inert in this build — the property ADR-129 D3c fixes.
 *
 * WHERE THE PROTECTION ACTUALLY COMES FROM, which is not where I first wrote it.
 * Ajv validates only the formats it has DEFINITIONS for, and it ships none: they live in the
 * separate `ajv-formats` package. So inertness comes from that package being ABSENT, and the
 * `validateFormats: false` flag above is defence-in-depth, not the gate.
 *
 * I learned this by trying to break the guard: flipping the flag to `true` changed nothing,
 * because there was still no `date-time` definition to apply. An earlier version of this
 * function claimed to "assert the posture" and would have returned the same answer either
 * way — a check that cannot fail is worse than no check, because it reads as coverage.
 *
 * `formatIsAssertedWhenRegistered` below demonstrates the other half: register a format and
 * it IS enforced. Together they locate the real gate, which is why the parity suite's
 * load-bearing assertion is the one on package.json.
 */
export function formatsAreInert(): boolean {
  const validate = makeAjv().compile({ type: 'string', format: 'date-time' })
  return validate('nope') === true
}

/** Proof that inertness is a consequence of no registered formats, not of the flag: give Ajv
 *  a definition and the same schema starts rejecting. Exported for the parity suite so the
 *  claim above is demonstrated rather than asserted in a comment. */
export function formatIsAssertedWhenRegistered(): boolean {
  const ajv = new Ajv2020({ allErrors: true, strict: false })
  ajv.addFormat('date-time', /^\d{4}-\d{2}-\d{2}T/)
  const validate = ajv.compile({ type: 'string', format: 'date-time' })
  return validate('nope') === false
}

/**
 * The part of a data document that IS the instance — what the canonical validator checks.
 *
 * `$schema_ref` and `owner` are registry metadata ABOUT the document, not records inside it,
 * and `validate_data_file` in scripts/validate-reference-data.py strips both before the
 * schema/instance walk. Anything in the browser that checks the raw document instead is asking
 * a different question from the authority, and the difference is not academic: a schema that
 * closes its root with `additionalProperties: false` then fails on those two keys, and the
 * operator cannot clear it — removing `$schema_ref` to satisfy the schema is itself refused,
 * because a published document may not change its binding (ADR-168 D6).
 *
 * It lives here, next to the validator, so the three callers share one projection. ADR-129's
 * finding was that a rule which is awkward to reach grows copies; three hand-rolled strips
 * would be exactly that, one keystroke from drifting apart.
 *
 * A non-object document is returned untouched: `type` is then the finding worth reporting,
 * and quietly replacing it with `{}` would report the wrong one.
 */
export function registryInstance(doc: unknown): unknown {
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return doc
  return Object.fromEntries(
    Object.entries(doc as Record<string, unknown>)
      .filter(([k]) => k !== '$schema_ref' && k !== 'owner'))
}

/** At most this many findings, and this many characters, reach a commit message. */
const NOTE_FINDING_LIMIT = 5
const NOTE_CHAR_LIMIT = 800

/**
 * A verdict's findings, short enough to put in a commit message.
 *
 * The editor shows every finding — it has room and the operator needs them all. A PR body does
 * not: one acknowledged bulk edit produced 6,570 findings and 336,148 characters, and GitHub
 * rejects a body that size. The failure is worse than it sounds, because the propose handler
 * COMMITS before it opens the PR, so a rejected body leaves a committed branch with no pull
 * request and every retry adds another one.
 *
 * So the note carries a sample and an honest count of what it left out. A truncated list that
 * did not say it was truncated would read as the whole story.
 */
export function summarizeFindings(findings: SchemaFinding[]): string {
  const shown: string[] = []
  let used = 0
  for (const f of findings) {
    if (shown.length >= NOTE_FINDING_LIMIT) break
    const text = `${f.pointer || '(document)'} ${f.message}`
    if (used + text.length > NOTE_CHAR_LIMIT) break
    shown.push(text)
    used += text.length
  }
  const omitted = findings.length - shown.length
  if (shown.length === 0) {
    // Nothing fit — a single finding can be longer than the whole budget. Leading with the
    // separator ("; and 1 more not listed here") reads as a dropped first clause, so say the
    // one true thing instead. The findings are all still on screen.
    return omitted > 0 ? `${omitted} finding(s), too long to list here` : ''
  }
  const tail = omitted > 0 ? `; and ${omitted} more not listed here` : ''
  return `${shown.join('; ')}${tail}`
}

function toFinding(err: ErrorObject): SchemaFinding {
  return {
    pointer: err.instancePath || '',
    keyword: err.keyword,
    message: err.message ?? '',
  }
}

/**
 * Validate one data document against its COMPILED schema.
 *
 * A schema Ajv cannot compile is reported as a finding rather than thrown: the Data area is
 * a live editor, and an exception mid-keystroke would blank the form. The finding names the
 * schema as the problem so nobody spends time hunting the data.
 */
export function validateAgainstCompiled(compiledSchema: object, data: unknown): SchemaVerdict {
  let validate = compiledCache.get(compiledSchema)
  if (!validate) {
    try {
      validate = makeAjv().compile(compiledSchema)
      compiledCache.set(compiledSchema, validate)
    } catch (err) {
      return {
        valid: false,
        findings: [{
          pointer: '',
          keyword: 'schema',
          message: `this schema could not be compiled, so the data was not checked: ` +
            `${err instanceof Error ? err.message : String(err)}`,
        }],
      }
    }
  }
  const ok = validate(data)
  if (ok) return { valid: true, findings: [] }
  const errors = validate.errors ?? []
  // Sorted by pointer so the form's error list is stable between keystrokes — a list that
  // reorders as you type is one you cannot read.
  const findings = errors.map(toFinding)
    .sort((a, b) => a.pointer.localeCompare(b.pointer) || a.keyword.localeCompare(b.keyword))
  return { valid: false, findings }
}

/** The distinct instance pointers with a finding — what the form marks up. */
export function failingPointers(verdict: SchemaVerdict): string[] {
  return Array.from(new Set(verdict.findings.map((f) => f.pointer))).sort()
}
